import React from 'react';
import BulkPicker from '../BulkPicker/BulkPicker';
import { mailMergeApi } from '../../requests/mailMerge';

// The most the investors endpoint hands back at once.
const PER_PAGE = 100;
const SEARCH_DELAY_MS = 300;

const idOf = (investor) => String(investor.ARCIMEDES_ROW_ID);
const hasEmail = (investor) => Boolean((investor.EMAIL || '').trim());
const placeOf = (investor) => [investor.CITY, investor.STATE].filter(Boolean).join(', ');

/**
 * Add investors to a mail merge by hand, the way Loan Bids adds lender
 * contacts: the list is there on open, narrowed by state and by search.
 * There are far too many investors to load at once the way Loan Bids loads
 * its lenders, so the server filters and pages, and "Show more" brings the
 * next page. Ticks are kept across filters until Add.
 *
 * An investor with no email is shown but cannot be ticked: the merge would
 * only list them under "No email".
 */
export default function InvestorPicker({ takenIds, onAdd, onCancel }) {
    const [states, setStates] = React.useState([]);
    const [state, setState] = React.useState('');
    const [query, setQuery] = React.useState('');
    const [results, setResults] = React.useState(null);
    const [page, setPage] = React.useState(1);
    const [total, setTotal] = React.useState(0);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [failed, setFailed] = React.useState(false);
    // Which filters the list answers, so a slow earlier answer cannot replace a newer one.
    const requestRef = React.useRef(0);

    React.useEffect(() => {
        let live = true;
        mailMergeApi.investorStates()
            .then((codes) => { if (live) setStates(codes); })
            .catch(() => {});
        return () => {
            live = false;
            // Nothing still in flight may land after the picker closes.
            requestRef.current++;
        };
    }, []);

    const fetchPage = (nextPage) => mailMergeApi.searchInvestors({
        search: query.trim(),
        state,
        page: nextPage,
        perPage: PER_PAGE,
    });

    React.useEffect(() => {
        const requestId = ++requestRef.current;
        setFailed(false);
        setLoadingMore(false);

        const timer = setTimeout(() => {
            fetchPage(1)
                .then((envelope) => {
                    if (requestId !== requestRef.current) return;
                    setResults(envelope.data || []);
                    setPage(1);
                    setTotal(((envelope.meta || {}).pagination || {}).total || 0);
                })
                .catch(() => { if (requestId === requestRef.current) setFailed(true); });
        }, SEARCH_DELAY_MS);

        return () => clearTimeout(timer);
        // fetchPage reads query and state, the two things this runs on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, state]);

    const showMore = () => {
        const requestId = requestRef.current;
        setLoadingMore(true);

        fetchPage(page + 1)
            .then((envelope) => {
                if (requestId !== requestRef.current) return;
                setResults((current) => (current || []).concat(envelope.data || []));
                setPage(page + 1);
            })
            .catch(() => { if (requestId === requestRef.current) setFailed(true); })
            .finally(() => { if (requestId === requestRef.current) setLoadingMore(false); });
    };

    const shownCount = (results || []).length;

    return (
        <BulkPicker
            classPrefix="mm-investors"
            testId="mail-merge-investor-picker"
            groupLabel="Investors"
            filters={(
                <React.Fragment>
                    <select
                        className="mm-investors-state"
                        aria-label="State"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                    >
                        <option value="">All states</option>
                        {states.map((code) => <option key={code} value={code}>{code}</option>)}
                    </select>
                    <input
                        type="text"
                        className="mm-investors-search"
                        placeholder="Search name, email, company or address..."
                        aria-label="Search investors"
                        value={query}
                        autoFocus
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </React.Fragment>
            )}
            items={results || []}
            idOf={idOf}
            takenIds={takenIds}
            isDisabled={(investor) => !hasEmail(investor)}
            disabledReason="Can't be added: this investor has no email address on file"
            // Only the first load blanks the list; a refetch keeps the rows on
            // screen until the new ones arrive.
            loading={results === null && !failed}
            failed={failed}
            failedText="Could not load investors."
            emptyText="No investors match."
            footnote={results && total > shownCount ? (
                <React.Fragment>
                    {'Showing ' + shownCount.toLocaleString() + ' of ' + total.toLocaleString() + '. '}
                    <button
                        type="button"
                        className="mm-investors-more"
                        onClick={showMore}
                        disabled={loadingMore}
                    >
                        {loadingMore ? 'Loading…' : 'Show more'}
                    </button>
                </React.Fragment>
            ) : null}
            columns="20px minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 1.4fr) minmax(0, 0.9fr)"
            renderItem={(investor) => (
                <React.Fragment>
                    <span className="mm-investors-name">{investor.NAME || '—'}</span>
                    <span className="mm-investors-firm">{investor.FIRM_NAME || '—'}</span>
                    <span className="mm-investors-email">{hasEmail(investor) ? investor.EMAIL : 'No email on file'}</span>
                    <span className="mm-investors-place">{placeOf(investor) || '—'}</span>
                </React.Fragment>
            )}
            onAdd={onAdd}
            onCancel={onCancel}
        />
    );
}
